import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import type { PromptInputMessage } from '@crowdex/ui/ai/prompt-input';
import { toast } from '@crowdex/ui/components/toaster';
import type { ChatRequestOptions } from 'ai';
import { rewindChanges } from '@/lib/conversations/message-changes';
import { personaMentionOf } from '@/lib/conversations/persona-reply-client';
import {
  composerSubmitAction,
  composerSubmitIgnored,
  resolveComposerKeyAction,
  resolveFollowUpMode,
} from '@/lib/desktop-shell/composer-submit-decision';
import { selectWorktreeSourceRoot } from '@/lib/desktop-shell/conversation-worktree';
import type { ConversationState, DesktopShellStore } from '@/lib/desktop-shell/store';
import { flushPendingComposerDraft } from '@/lib/desktop-shell/use-composer-draft';
import { isOnline } from '@/lib/online-status';
import { formatResponseAnnotatedPrompt } from '@/lib/response-annotations';

export type ComposerSubmit = {
  handleSubmit: (message: PromptInputMessage) => void;
  handleKeyDown: (event: KeyboardEvent) => void;
};

/**
 * The composer submit/keydown command boundary for the desktop shell.
 *
 * All branching is delegated to the pure decisions in
 * `composer-submit-decision.ts` (`composerSubmitIgnored`, `composerSubmitAction`,
 * `resolveComposerKeyAction`), which are
 * exhaustively unit tested. This factory is the thin imperative glue that reads
 * store-backed composer state lazily via `store.getState()` (mirroring
 * `execution-mode-controls`/`branch-controls`) and fans a resolved action out to
 * the injected callbacks/refs that must stay tied to the live `useChat` state,
 * the ACP connection, and the DOM (steer/queue, message editing, worktree
 * provisioning, and form submission).
 */
export function createComposerSubmit(deps: {
  store: DesktopShellStore;
  preview: boolean;
  busy: boolean;
  canSubmit: boolean;
  /** Conversation whose draft this composer edits (its `paneDrafts` entry). */
  conversationId: string;
  /** The message being edited in this conversation, or `null`. */
  editingMessageId: string | null;
  oppositeFollowUpModeRef: { current: boolean };
  messages: readonly CrowdexUIMessage[];
  setMessages: (
    messages: CrowdexUIMessage[] | ((current: CrowdexUIMessage[]) => CrowdexUIMessage[]),
  ) => void;
  /** Resolves `false` when nothing was sent (its cloud sandbox could not start). */
  submitMessage: (message: PromptInputMessage, options?: ChatRequestOptions) => unknown;
  enqueueFollowUp: (conversationId: string, message: PromptInputMessage, id?: string) => void;
  removeQueuedFollowUp: (conversationId: string, id: string) => void;
  steerFollowUp: (message: PromptInputMessage, id?: string) => Promise<boolean>;
  prepareManagedWorktree: (sourceRoot?: string | null) => Promise<boolean>;
  /** The composer's picker shows (a / or @ being typed): it took its own keys first. */
  pickerOpen: boolean;
  cancelMessageEdit: () => void;
  /**
   * The first message of a new conversation really left (sent, or its worktree made): the
   * conversation is about to exist. Not called when the send is ignored, queued or an edit.
   */
  onFirstMessageSent?: () => void;
  /**
   * `@persona` (design D51). A persona on the conversation's runtime takes its turn: the message
   * goes as any other, its transport marks the turn (`personaRunsHere`). One on another runtime
   * answers aside, read-only (`askPersona`): the user's message and its reply, as stored.
   */
  personaRunsHere?: (profileId: string) => Promise<boolean>;
  askPersona?: (
    profileId: string,
    message: { id: string; text: string },
  ) => Promise<CrowdexUIMessage[]>;
}): ComposerSubmit {
  const submittable = (): boolean => deps.canSubmit;

  function setDraft(value: string | ((previous: string) => string)): void {
    flushPendingComposerDraft(deps.store, deps.conversationId);
    const { paneDrafts, setPaneDrafts } = deps.store.getState();
    const previous = paneDrafts[deps.conversationId] ?? '';
    const next = typeof value === 'function' ? value(previous) : value;
    setPaneDrafts((current) => ({ ...current, [deps.conversationId]: next }));
  }

  function handleSubmit(message: PromptInputMessage): void {
    const followUpMode = resolveFollowUpMode(deps.oppositeFollowUpModeRef.current);
    deps.oppositeFollowUpModeRef.current = false;
    const editingMessageId = deps.editingMessageId;
    const state = deps.store.getState();
    const {
      elementSelections,
      responseAnnotations,
      executionMode,
      setElementSelections,
      setResponseAnnotations,
    } = state;
    const worktreeSourceRoot = selectWorktreeSourceRoot(state);

    if (
      composerSubmitIgnored({
        preview: deps.preview,
        busy: deps.busy,
        canSubmit: submittable(),
        hasText: message.text.trim().length > 0,
        fileCount: message.files.length,
        elementSelectionCount: elementSelections.length,
        annotationCount: responseAnnotations.length,
      })
    ) {
      return;
    }

    setDraft('');
    const selectionFiles = elementSelections.flatMap((selection, index) =>
      selection.dataUrl === undefined
        ? []
        : [
            {
              type: 'file' as const,
              url: selection.dataUrl,
              mediaType: 'image/png',
              filename: `element-selection-${index + 1}.png`,
            },
          ],
    );
    const selectionContext = elementSelections.map((selection) => selection.context).join('\n\n');
    setElementSelections([]);
    const annotatedPrompt = formatResponseAnnotatedPrompt(
      message.text.trimEnd(),
      responseAnnotations.map(({ annotation, text }) => ({ annotation, text })),
    );
    setResponseAnnotations([]);
    const submittedMessage = {
      text: [annotatedPrompt, selectionContext].filter(Boolean).join('\n\n'),
      files: [...message.files, ...selectionFiles],
    };

    const editedIndex =
      editingMessageId !== null
        ? deps.messages.findIndex((candidate) => candidate.id === editingMessageId)
        : -1;
    const action = composerSubmitAction({
      busy: deps.busy,
      followUpMode,
      editingMessageId,
      editedIndex,
      messagesLength: deps.messages.length,
      executionMode,
      worktreeSourceRoot,
    });

    // `@Designer …` (design D51): a turn of the persona, or its read-only reply aside. Where it
    // runs is known once the saved agents are read, never from what an empty cache says.
    const send = (personaTurn: boolean) =>
      route(message, submittedMessage, action, editingMessageId, personaTurn);
    const persona = action.type === 'edit' ? null : personaMentionOf(submittedMessage.text);
    if (persona === null) {
      send(false);
      return;
    }
    void (deps.personaRunsHere?.(persona.id) ?? Promise.resolve(false))
      .catch(() => false)
      .then((personaTurn) => {
        if (personaTurn || deps.askPersona === undefined) {
          send(personaTurn);
          return;
        }
        // Aside, the reply needs the conversation stored, and reads no attachment.
        if (deps.messages.length === 0 || submittedMessage.files.length > 0) {
          setDraft(message.text);
          toast.error(
            `${persona.label} cannot answer here`,
            deps.messages.length === 0
              ? 'Start the conversation first, then bring them in with @.'
              : 'They run on another agent and answer without attachments.',
          );
          return;
        }
        void askPersonaOnce(deps.askPersona, persona, submittedMessage.text);
      });
  }

  /** Where the submitted message goes: the queue, a steer, an edit, a worktree, or the agent. */
  function route(
    message: PromptInputMessage,
    submittedMessage: { text: string; files: PromptInputMessage['files'] },
    action: ReturnType<typeof composerSubmitAction>,
    editingMessageId: string | null,
    personaTurn: boolean,
  ): void {
    const { setEditingMessageId, setConversationState } = deps.store.getState();
    const update = (patch: Partial<ConversationState>) =>
      setConversationState(deps.conversationId, patch);
    // Offline, a new message waits in the queue and goes once the network is back (D53). A
    // persona's message never steers the agent's turn: it waits for its own.
    if (
      action.type === 'queue' ||
      (action.type === 'steer' && personaTurn) ||
      (action.type === 'submit' && !isOnline())
    ) {
      const followUpId = crypto.randomUUID();
      deps.enqueueFollowUp(deps.conversationId, submittedMessage, followUpId);
      return;
    }
    if (action.type === 'steer') {
      const followUpId = crypto.randomUUID();
      // Keep the message visible and lossless while delivery crosses the
      // local process or the cloud Valkey inbox. Unsupported/raced turns stay
      // queued; an accepted steer is removed from the follow-up queue.
      deps.enqueueFollowUp(deps.conversationId, submittedMessage, followUpId);
      void deps
        .steerFollowUp(submittedMessage, followUpId)
        .then((accepted) => {
          if (accepted) deps.removeQueuedFollowUp(deps.conversationId, followUpId);
        })
        .catch(() => undefined);
      return;
    }

    if (editingMessageId !== null) setEditingMessageId(null);
    if (action.type === 'edit') {
      // The turns after the edited message go: the user sees what is lost (their files too) and
      // picks whether the edit continues here or starts a new conversation (design D46).
      const changes = rewindChanges(deps.messages, action.editedIndex);
      const turnCount = deps.messages
        .slice(action.editedIndex + 1)
        .filter((candidate) => candidate.role === 'assistant').length;
      if (turnCount > 0) {
        update({
          pendingRewind: {
            message: submittedMessage,
            messageId: deps.messages[action.editedIndex]?.id ?? '',
            editedIndex: action.editedIndex,
            files: changes?.files ?? [],
            turnCount,
            cancel: () => {
              setEditingMessageId(editingMessageId);
              setDraft(message.text);
            },
          },
        });
        return;
      }
      deps.setMessages(deps.messages.slice(0, action.editedIndex));
      update({ pendingSend: { kind: 'edited', message: submittedMessage, ready: true } });
      return;
    }
    if (action.type === 'provision-worktree') {
      // It leaves once the conversation's worktree is in place (`usePendingMessageDrain`), even
      // when the user opened another conversation meanwhile.
      update({ pendingSend: { kind: 'worktree', message: submittedMessage, ready: false } });
      // A worktree that could not be made sends nothing: the message goes back to the composer.
      const first = deps.messages.length === 0;
      void deps.prepareManagedWorktree().then((made) => {
        if (!made) setDraft(message.text);
        else if (first) deps.onFirstMessageSent?.();
      });
      return;
    }
    if (deps.messages.length === 0) deps.onFirstMessageSent?.();
    // Not sent (no cloud sandbox to start, H6): the message goes back to the composer, unless
    // something new was typed meanwhile, while the banner says why and what to do.
    void Promise.resolve(deps.submitMessage(submittedMessage)).then((sent) => {
      if (sent === false) setDraft((current) => (current === '' ? message.text : current));
    });
  }

  /**
   * The persona's read-only reply: the message and a reply waiting for it are added at once,
   * then the stored pair takes their place; a failure says so in the reply. What streamed in the
   * meantime stays where it is.
   */
  async function askPersonaOnce(
    ask: NonNullable<typeof deps.askPersona>,
    persona: { id: string; label: string },
    text: string,
  ): Promise<void> {
    const user: CrowdexUIMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      parts: [{ type: 'text', text }],
    };
    const waitingId = `${user.id}-persona`;
    const waiting = (reply: string): CrowdexUIMessage => ({
      id: waitingId,
      role: 'assistant',
      parts: [{ type: 'text', text: reply }],
      metadata: {
        persona: { id: persona.id, name: persona.label, agentName: null, readOnly: true },
      },
    });
    const replace = (next: readonly CrowdexUIMessage[]) =>
      deps.setMessages((current) => {
        const at = current.findIndex((entry) => entry.id === user.id);
        const kept = current.filter((entry) => entry.id !== user.id && entry.id !== waitingId);
        if (at < 0) return [...kept, ...next];
        return [...kept.slice(0, at), ...next, ...kept.slice(at)];
      });
    deps.setMessages((current) => [...current, user, waiting('…')]);
    try {
      replace(await ask(persona.id, { id: user.id, text }));
    } catch (cause) {
      replace([
        user,
        waiting(
          `${persona.label} could not answer: ${cause instanceof Error ? cause.message : String(cause)}`,
        ),
      ]);
    }
  }

  /** A phone's or tablet's own keyboard: nothing hovers, the pointer is a finger. */
  function touchKeyboard(): boolean {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: none) and (pointer: coarse)').matches
    );
  }

  function requestSubmit(event: KeyboardEvent): void {
    (event.currentTarget as HTMLElement | null)?.closest('form')?.requestSubmit();
  }

  function handleKeyDown(event: KeyboardEvent): void {
    const action = resolveComposerKeyAction({
      key: event.key,
      shiftKey: event.shiftKey,
      metaKey: event.metaKey,
      ctrlKey: event.ctrlKey,
      editing: deps.editingMessageId !== null,
      busy: deps.busy,
      pickerOpen: deps.pickerOpen,
      touchKeyboard: touchKeyboard(),
    });

    switch (action.type) {
      case 'none':
        return;
      case 'cancel-edit':
        event.preventDefault();
        deps.cancelMessageEdit();
        return;
      case 'submit-opposite-follow-up':
        event.preventDefault();
        deps.oppositeFollowUpModeRef.current = true;
        requestSubmit(event);
        return;
      case 'submit':
        event.preventDefault();
        requestSubmit(event);
        return;
    }
  }

  return { handleSubmit, handleKeyDown };
}
